import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { PARTS } from '../data/parts';
import Model3DModal from '../components/viewer3d/Model3DModal';
import { ForgeLegendModal } from '../components/legend';
import ShareModal from '../components/share/ShareModal';
import styles from './DetailPage.module.css';

export default function DetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { isLoggedIn, user } = useAuth();

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [show3DModal, setShow3DModal] = useState(false);
    const [uploading3D, setUploading3D] = useState(false);
    const [showForgeLegend, setShowForgeLegend] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    useEffect(() => {
        fetchConfiguration();
        fetchComments();
    }, [id]);

    async function fetchConfiguration() {
        setLoading(true);
        try {
            const result = await api.getConfiguration(id);
            if (result.success) {
                setConfig(result.data);
            } else {
                setError(result.error || 'Failed to load configuration');
            }
        } catch (err) {
            setError('Failed to load configuration');
        } finally {
            setLoading(false);
        }
    }

    async function fetchComments() {
        try {
            const result = await api.getComments(id);
            if (result.success) {
                setComments(result.data || []);
            }
        } catch (err) {
            console.error('Failed to load comments:', err);
        }
    }

    async function handleLike() {
        if (!isLoggedIn) return;
        try {
            const result = await api.toggleLike(id);
            if (result.success) {
                setConfig(prev => ({
                    ...prev,
                    hasLiked: result.liked,
                    likes_count: prev.likes_count + (result.liked ? 1 : -1),
                }));
            }
        } catch (err) {
            console.error('Like failed:', err);
        }
    }

    async function handleFavorite() {
        if (!isLoggedIn) return;
        try {
            const result = await api.toggleFavorite(id);
            if (result.success) {
                setConfig(prev => ({
                    ...prev,
                    hasFavorited: result.favorited,
                    favorites_count: prev.favorites_count + (result.favorited ? 1 : -1),
                }));
            }
        } catch (err) {
            console.error('Favorite failed:', err);
        }
    }

    async function handleSubmitComment(e) {
        e.preventDefault();
        if (!newComment.trim() || !isLoggedIn) return;

        setSubmittingComment(true);
        try {
            const result = await api.addComment(id, newComment.trim());
            if (result.success) {
                setComments(prev => [result.data, ...prev]);
                setNewComment('');
                setConfig(prev => ({
                    ...prev,
                    comments_count: prev.comments_count + 1,
                }));
            }
        } catch (err) {
            console.error('Comment failed:', err);
        } finally {
            setSubmittingComment(false);
        }
    }

    function handleApplyConfig() {
        if (config?.config_data) {
            // 导航到3D预览页面并应用此配置
            navigate('/3d-preview', {
                state: {
                    presetConfig: config.config_data,
                    fromConfigDetail: true,
                    configTitle: config.title
                }
            });
        }
    }

    // 铸造传奇回调
    function handleForgeLegendSuccess(legend) {
        console.log('Legend forged:', legend);
        // 可以刷新页面或更新状态
    }

    function handleForgeLegendClose() {
        setShowForgeLegend(false);
    }

    // 判断是否可以铸造传奇（作者本人且已审核通过）
    const canForgeLegend = isLoggedIn &&
        user?.id === config?.user_id &&
        config?.status === 'approved' &&
        !config?.legend_id; // 还没有铸造过

    async function handle3DModelUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;

        // Client-side validation
        if (!file.name.toLowerCase().endsWith('.glb')) {
            alert(t('detail.invalidGLB') || 'Please select a GLB file');
            return;
        }

        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
            alert(t('detail.fileTooLarge') || 'File size must be under 100MB');
            return;
        }

        setUploading3D(true);
        try {
            const result = await api.upload3DModel(id, file);
            if (result.success) {
                setConfig(prev => ({
                    ...prev,
                    model3d_url: result.data.model3d_url,
                    model3d_size: result.data.model3d_size,
                    model3d_metadata: result.data.model3d_metadata,
                }));
            } else {
                alert(result.error || 'Upload failed');
            }
        } catch (err) {
            console.error('3D model upload failed:', err);
            alert(t('detail.uploadFailed') || 'Upload failed');
        } finally {
            setUploading3D(false);
            // Reset file input
            e.target.value = '';
        }
    }

    async function handleDelete3DModel() {
        if (!confirm(t('detail.confirmDelete3D') || 'Are you sure you want to delete the 3D model?')) {
            return;
        }

        try {
            const result = await api.delete3DModel(id);
            if (result.success) {
                setConfig(prev => ({
                    ...prev,
                    model3d_url: null,
                    model3d_size: null,
                    model3d_metadata: null,
                }));
            } else {
                alert(result.error || 'Delete failed');
            }
        } catch (err) {
            console.error('3D model delete failed:', err);
            alert(t('detail.deleteFailed') || 'Delete failed');
        }
    }

    function getPartName(category, partId) {
        const part = PARTS[category]?.find(p => p.id === partId);
        return part ? t(part.nameKey) || part.id : partId;
    }

    if (loading) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>{t('common.loading') || 'Loading...'}</p>
            </div>
        );
    }

    if (error || !config) {
        return (
            <div className={styles.error}>
                <h2>{t('error.notFound') || 'Configuration not found'}</h2>
                <p>{error}</p>
                <Link to="/gallery" className={styles.backBtn}>
                    {t('common.back') || 'Back to Gallery'}
                </Link>
            </div>
        );
    }

    const media = config.media || [];
    const configData = config.config_data || {};

    return (
        <div className={styles.detail}>
            <div className={styles.container}>
                {/* Left: Media Gallery */}
                <div className={styles.mediaSection}>
                    <div className={styles.mainMedia}>
                        {media.length > 0 ? (
                            media[currentMediaIndex]?.media_type === 'video' ? (
                                <video
                                    src={media[currentMediaIndex].url}
                                    controls
                                    className={styles.mainImage}
                                />
                            ) : (
                                <img
                                    src={media[currentMediaIndex]?.url || config.cover_image_url}
                                    alt={config.title}
                                    className={styles.mainImage}
                                />
                            )
                        ) : config.cover_image_url ? (
                            <img
                                src={config.cover_image_url}
                                alt={config.title}
                                className={styles.mainImage}
                            />
                        ) : (
                            <div className={styles.noImage}>{t('common.noImage')}</div>
                        )}

                        {config.is_official && (
                            <span className={styles.officialBadge}>{t('common.official')}</span>
                        )}
                        {/* 显示配置状态徽章（草稿、待审核等） */}
                        {config.status && config.status !== 'approved' && (
                            <span className={`${styles.statusBadge} ${styles[config.status]}`}>
                                {config.status === 'draft' ? (t('profile.statusDraft') || 'Draft') :
                                 config.status === 'pending' ? (t('profile.statusPending') || 'Pending') :
                                 config.status === 'rejected' ? (t('profile.statusRejected') || 'Rejected') :
                                 config.status}
                            </span>
                        )}
                    </div>

                    {media.length > 1 && (
                        <div className={styles.thumbnails}>
                            {media.map((m, index) => (
                                <button
                                    key={m.id}
                                    className={`${styles.thumbnail} ${index === currentMediaIndex ? styles.active : ''}`}
                                    onClick={() => setCurrentMediaIndex(index)}
                                >
                                    {m.media_type === 'video' ? (
                                        <div className={styles.videoThumb}>▶</div>
                                    ) : (
                                        <img src={m.thumbnail_url || m.url} alt="" />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right: Info & Actions */}
                <div className={styles.infoSection}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>{config.title}</h1>
                        <div className={styles.author}>
                            <span className={styles.avatar}>
                                {config.user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                            <span>{config.user?.nickname || t('common.anonymous')}</span>
                        </div>
                    </div>

                    {config.description && (
                        <p className={styles.description}>{config.description}</p>
                    )}

                    {/* Configuration Summary */}
                    <div className={styles.configSummary}>
                        <h3>{t('detail.configuration') || 'Configuration'}</h3>
                        <div className={styles.configList}>
                            {/* Body Color - 车身颜色 */}
                            {configData.body && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('categories.body') || 'Body Color'}</span>
                                    <span className={styles.configValue}>{getPartName('body', configData.body)}</span>
                                </div>
                            )}
                            {/* Trim - 装饰风格 */}
                            {configData.trim && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('categories.trim') || 'Trim Style'}</span>
                                    <span className={styles.configValue}>{getPartName('trim', configData.trim)}</span>
                                </div>
                            )}
                            {/* Performance - 性能配件 */}
                            {configData.performance && configData.performance !== 'none' && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('categories.performance') || 'Performance'}</span>
                                    <span className={styles.configValue}>
                                        {Array.isArray(configData.performance)
                                            ? configData.performance.filter(p => p !== 'none').map(p => getPartName('performance', p)).join(', ')
                                            : getPartName('performance', configData.performance)
                                        }
                                    </span>
                                </div>
                            )}
                            {/* Comfort - 舒适配件 */}
                            {configData.comfort?.length > 0 && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('categories.comfort') || 'Comfort'}</span>
                                    <span className={styles.configValue}>
                                        {configData.comfort.map(c => getPartName('comfort', c)).join(', ')}
                                    </span>
                                </div>
                            )}
                            {/* Style - 风格配件 */}
                            {configData.style?.length > 0 && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('categories.style') || 'Style'}</span>
                                    <span className={styles.configValue}>
                                        {configData.style.map(s => getPartName('style', s)).join(', ')}
                                    </span>
                                </div>
                            )}
                            {/* Custom Color - 自定义颜色 */}
                            {configData.customization?.selectedColor === 'custom-color' && configData.customization?.customColorHex && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('detail.customColor') || 'Custom Color'}</span>
                                    <span className={styles.configValue} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                backgroundColor: configData.customization.customColorHex,
                                                borderRadius: '4px',
                                                border: '1px solid #444',
                                                display: 'inline-block'
                                            }}
                                        />
                                        {configData.customization.customColorHex}
                                    </span>
                                </div>
                            )}
                            {/* Custom Texture - 自定义纹理 */}
                            {configData.customization?.selectedColor === 'custom-pattern' && configData.customization?.customTextureUrl && (
                                <div className={styles.configItem}>
                                    <span className={styles.configLabel}>{t('detail.customTexture') || 'Custom Texture'}</span>
                                    <span className={styles.configValue}>
                                        <img
                                            src={configData.customization.customTextureUrl}
                                            alt="Custom texture"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                border: '1px solid #444'
                                            }}
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className={styles.totalPrice}>
                            <span>{t('detail.totalPrice') || 'Total Price'}</span>
                            <span className={styles.price}>¥{(config.total_price || 0).toLocaleString()}</span>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={styles.actions}>
                        <button
                            className={`${styles.actionBtn} ${config.hasLiked ? styles.liked : ''}`}
                            onClick={handleLike}
                            disabled={!isLoggedIn}
                        >
                            <span>{config.hasLiked ? '❤️' : '🤍'}</span>
                            <span>{config.likes_count || 0}</span>
                        </button>
                        <button
                            className={`${styles.actionBtn} ${config.hasFavorited ? styles.favorited : ''}`}
                            onClick={handleFavorite}
                            disabled={!isLoggedIn}
                        >
                            <span>{config.hasFavorited ? '⭐' : '☆'}</span>
                            <span>{config.favorites_count || 0}</span>
                        </button>
                        <button className={styles.applyBtn} onClick={handleApplyConfig}>
                            {t('detail.applyConfig') || 'Apply Configuration'}
                        </button>
                        <button
                            className={styles.shareBtn}
                            onClick={() => setShowShareModal(true)}
                            title={t('detail.share') || 'Share'}
                        >
                            <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19071C7.54305 8.46696 6.6582 8 5.66667 8C4.19391 8 3 9.19391 3 10.6667C3 12.1394 4.19391 13.3333 5.66667 13.3333C6.6582 13.3333 7.54305 12.8663 8.08261 12.1426L15.0227 15.9627C15.0077 16.0842 15 16.2078 15 16.3333C15 17.806 16.194 19 17.6667 19C19.1394 19 20.3333 17.806 20.3333 16.3333C20.3333 14.8607 19.1394 13.6667 17.6667 13.6667C16.6752 13.6667 15.7904 14.1337 15.2508 14.8574L8.31073 11.0373C8.32566 10.9159 8.33333 10.7922 8.33333 10.6667C8.33333 10.5412 8.32566 10.4175 8.31073 10.296L15.2508 6.47593C15.7904 7.19968 16.6752 7.66667 17.6667 7.66667C17.7855 7.66667 17.903 7.66027 18.0189 7.64778" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        {canForgeLegend && (
                            <button
                                className={styles.forgeLegendBtn}
                                onClick={() => setShowForgeLegend(true)}
                            >
                                🔥 {t('detail.forgeLegend') || '铸造传奇'}
                            </button>
                        )}
                        {config?.legend_id && (
                            <Link
                                to={`/legend/${config.legend_id}`}
                                className={styles.viewLegendBtn}
                            >
                                🏆 {t('detail.viewLegend') || '查看传奇'}
                            </Link>
                        )}
                    </div>

                    {/* 3D Model Section */}
                    {config.model3d_url ? (
                        <div className={styles.model3dSection}>
                            <button
                                className={styles.view3dBtn}
                                onClick={() => setShow3DModal(true)}
                            >
                                <span className={styles.icon3d}>🎮</span>
                                {t('detail.view3D') || 'View 3D Model'}
                            </button>
                            {isLoggedIn && user?.id === config.user_id && (
                                <button
                                    className={styles.delete3dBtn}
                                    onClick={handleDelete3DModel}
                                >
                                    {t('detail.delete3D') || 'Delete 3D'}
                                </button>
                            )}
                        </div>
                    ) : isLoggedIn && user?.id === config.user_id && config.status === 'approved' && (
                        <div className={styles.model3dSection}>
                            <label className={styles.upload3dBtn}>
                                <input
                                    type="file"
                                    accept=".glb"
                                    onChange={handle3DModelUpload}
                                    disabled={uploading3D}
                                    style={{ display: 'none' }}
                                />
                                <span className={styles.icon3d}>📦</span>
                                {uploading3D
                                    ? (t('detail.uploading') || 'Uploading...')
                                    : (t('detail.upload3D') || 'Upload 3D Model')
                                }
                            </label>
                            <span className={styles.upload3dHint}>
                                {t('detail.upload3DHint') || 'GLB format, max 100MB'}
                            </span>
                        </div>
                    )}

                    {/* Stats */}
                    <div className={styles.stats}>
                        <span>👁 {config.views_count || 0} {t('detail.views') || 'views'}</span>
                        <span>💬 {config.comments_count || 0} {t('detail.comments') || 'comments'}</span>
                    </div>
                </div>
            </div>

            {/* Comments Section */}
            <div className={styles.commentsSection}>
                <h3>{t('detail.comments') || 'Comments'} ({config.comments_count || 0})</h3>

                {isLoggedIn ? (
                    <form className={styles.commentForm} onSubmit={handleSubmitComment}>
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder={t('detail.commentPlaceholder') || 'Write a comment...'}
                            rows={3}
                            maxLength={1000}
                        />
                        <button type="submit" disabled={!newComment.trim() || submittingComment}>
                            {submittingComment
                                ? (t('common.submitting') || 'Submitting...')
                                : (t('detail.submitComment') || 'Submit')
                            }
                        </button>
                    </form>
                ) : (
                    <p className={styles.loginPrompt}>
                        <Link to="#" onClick={() => {}}>
                            {t('detail.loginToComment') || 'Login to comment'}
                        </Link>
                    </p>
                )}

                <div className={styles.commentsList}>
                    {comments.length === 0 ? (
                        <p className={styles.noComments}>
                            {t('detail.noComments') || 'No comments yet. Be the first!'}
                        </p>
                    ) : (
                        comments.map(comment => (
                            <div key={comment.id} className={styles.comment}>
                                <div className={styles.commentHeader}>
                                    <span className={styles.commentAvatar}>
                                        {comment.user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                    <span className={styles.commentAuthor}>
                                        {comment.user?.nickname || t('common.anonymous')}
                                    </span>
                                    <span className={styles.commentDate}>
                                        {new Date(comment.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                <p className={styles.commentContent}>{comment.content}</p>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* 3D Model Viewer Modal */}
            {show3DModal && config.model3d_url && (
                <Model3DModal
                    isOpen={show3DModal}
                    onClose={() => setShow3DModal(false)}
                    modelUrl={config.model3d_url}
                    title={config.title}
                />
            )}

            {/* 铸造传奇弹窗 */}
            <ForgeLegendModal
                isOpen={showForgeLegend}
                onClose={handleForgeLegendClose}
                configurationId={id}
                configurationTitle={config?.title}
                configurationImage={config?.cover_image_url}
                onSuccess={handleForgeLegendSuccess}
            />

            {/* 分享弹窗 */}
            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                configId={id}
                configData={config?.config_data}
                coverImageUrl={config?.cover_image_url}
                totalPrice={config?.total_price}
                configTitle={config?.title}
            />
        </div>
    );
}
